import { parseChart, type Chart } from '@musicgame/chart';

interface LevelResource {
  data: Chart;
  audio: ArrayBuffer;
  offsetMs: number;
}

/**
 * Load a level's chart JSON and associated audio.
 *
 * @param name Combined artist and level identifier in the form
 * `<artist>/<level>`. The chart JSON is fetched from
 * `/levels/<artist>/<level>/level.json` and expected to follow the Song schema
 * containing an `audio` block and a `charts` array. The first chart in the
 * array is returned.
 */
export default async function loadLevel(name: string): Promise<LevelResource> {
  const jsonUrl = `/levels/${name}/level.json`;
  const res = await fetch(jsonUrl);
  if (!res.ok) {
    throw new Error('Failed to fetch level JSON');
  }

  const dataText = await res.text();
  let raw: any;
  try {
    raw = JSON.parse(dataText);
  } catch (err) {
    throw new Error(`Failed to parse chart JSON: ${(err as Error).message}`);
  }

  if (!raw.audio || typeof raw.audio.url !== 'string') {
    throw new Error('Missing audio.url in chart JSON');
  }
  if (!Array.isArray(raw.charts) || raw.charts.length === 0) {
    throw new Error('No charts in chart JSON');
  }

  const data = parseChart(raw.charts[0]);

  const audioRes = await fetch(raw.audio.url);
  if (!audioRes.ok) {
    throw new Error(`Failed to fetch level audio: ${audioRes.status}`);
  }
  const audio = await audioRes.arrayBuffer();

  const offsetMs: number = raw.audio.offsetMs ?? 0;

  return { data, audio, offsetMs };
}

